/**
 * @file OrderBook Component
 * @description A financial trading interface component that displays bid and ask orders
 * with collapsible panels, price summaries, and dynamic order filtering.
 *
 * @component Hierarchy
 * OrderBook (index.tsx)
 * └── OrderBookContent (index.tsx) - Uses Zustand store for state management
 *     ├── OrderBookHeader (styles.tsx, conditional)
 *     ├── OrderBookSummaryBanner (index.tsx)
 *     │   ├── SummaryBannerContainer (styles.tsx)
 *     │   ├── SummarySideBannerButtonContainer (styles.tsx) - Bids
 *     │   ├── SummaryMidContainer (styles.tsx) - Market Price
 *     │   └── SummarySideBannerButtonContainer (styles.tsx) - Asks
 *     └── OrderBookTables (index.tsx)
 *         ├── BidsTable (Table component)
 *         │   ├── TableHeader with column headers and HighlightBidRow
 *         │   └── TableBody with OrderBookCell rows (styles.tsx)
 *         └── AsksTable (Table component)
 *             ├── TableHeader with column headers and HighlightAskRow
 *             └── TableBody with OrderBookCell rows (styles.tsx)
 *
 * @state Management with Zustand
 * - store.ts: Zustand store with devtools and subscribeWithSelector middleware
 * - use-order-book-store.ts: Custom hooks for accessing store state
 * - State includes: activePanel, isCollapsed, orders, summaryData, isEmpty
 *
 * @utils
 * - buildOrderBook() (utils.ts): Transforms raw order data into display format
 * - buildSummaryData() (utils.ts): Calculates price summaries and ranges
 * - isPanelActive() (utils.ts): Determines if a panel should be displayed
 *
 * @example
 * // Basic usage
 * <OrderBook
 *   instrumentId=""
 *   hasBorder={true}
 *   rows={10}
 *   collapsible={true}
 * />
 */

import type { TraderProfile as MesaTraderProfile } from '@/types/api/legacy-trader-profile'
import type { AdvisorViewStatus } from '@/types/enums/advisor-view-status'
import type { OrderSide } from '@/types/enums/order'
import type { TraderViewStatus } from '@/types/enums/trader-view-status'

import { useMemo } from 'react'

import { useUserArchetype } from '@/hooks/use-user-archetype'
import { useUserRiskGroupId } from '@/hooks/use-user-risk-group-id'
import { useGetUnifiedMarketQuery } from '@/legacy-api/trading-slice'
import { UserArchetype } from '@/utils/trade-blotter/compute-order-status'

import { ControlPanel, useControlPanel } from './control-panel'
import { OrderBookHeader } from './order-book-header'
import { OrderBookSummaryBanner } from './order-book-summary-banner'
import { OrderBookTables } from './order-book-tables'
import { defaultEmptyOrder, defaultSummaryData } from './store'
import { OrderBookContainer, OrderBookTitle } from './styles'
import {
  ActivePanelType,
  type OrderBookProps,
  type OrderBookRowItem,
  type OrderSummaryData,
  QuoteSide,
} from './types'
import { useEnrichedFilteredBidsAsks } from './use-enriched-filtered-bids-asks'
import { useOrderBookState } from './use-order-book-store'
import {
  buildOrderBook,
  buildSummaryData,
  enrichOrderBookRowItems,
} from './utils'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function OrderBook({
  orders: initialOrders,
  instrumentId,
  riskGroupId: riskGroupIdProp,
  // If provided, visually disable orders below this quantity
  // If not provided, all orders are enabled
  minNumBonds,
  maxNumBonds,
  className,
  baseClassName,
  hasBorder = false,
  rows,
  collapsible = true,
  status,
  onRowAction,
  isButtonDisabled = false,
  showVenuesColumn = false,
  alwaysShowVenuesColumnForTrader = false,
  showSource = false,
  orderSide,
  showActionButtonsOnAllRows = false,
  hideActionButtonsForNonApplicableTradeSide = false,
  showPreAndPostFeeValues = false,
  titleText,
  secondaryText,
  isQuoteSnapshot,
  showControlPanel = false,
  onCollapse,
}: OrderBookProps) {
  const ORDER_BOOK_POLLING_INTERVAL = 1000

  // We will eventually have all callers of the order book specify a riskGroupId explicitly.
  // Until then, callers that don't pass it fall back to the user's default risk group.
  // When instrumentId is empty, we skip polling and fall back to initialOrders.
  const { riskGroupId: defaultRiskGroupId, isLoading: riskGroupLoading } =
    useUserRiskGroupId()
  const riskGroupId = riskGroupIdProp ?? defaultRiskGroupId
  const shouldPoll = instrumentId && !riskGroupLoading && riskGroupId
  const unifiedMarketQuery = useGetUnifiedMarketQuery(
    { id: instrumentId, riskGroupId },
    { skip: !shouldPoll, pollingInterval: ORDER_BOOK_POLLING_INTERVAL },
  )

  const { asks = [], bids = [] } =
    unifiedMarketQuery.data || initialOrders || {}

  // Determine if order book is empty
  const isEmpty = useMemo(
    () => asks?.length === 0 && bids?.length === 0,
    [asks, bids],
  )

  // Calculate order book data
  const orderBookOrders = useMemo(
    () => buildOrderBook({ bids, asks }, showPreAndPostFeeValues),
    [bids, asks, showPreAndPostFeeValues],
  )

  const summaryData = useMemo(() => {
    // TODO - should summary data update based on filter state?
    const enrichedBids = enrichOrderBookRowItems(
      orderBookOrders,
      minNumBonds,
      maxNumBonds,
      QuoteSide.Bid,
    )
    const enrichedAsks = enrichOrderBookRowItems(
      orderBookOrders,
      minNumBonds,
      maxNumBonds,
      QuoteSide.Ask,
    )
    return buildSummaryData(
      enrichedBids,
      enrichedAsks,
      bids.length,
      asks.length,
    )
  }, [orderBookOrders, minNumBonds, maxNumBonds, bids.length, asks.length])

  // Prepare display data based on whether the order book is empty
  const displaySummaryData = useMemo(
    () => (isEmpty ? defaultSummaryData : summaryData),
    [isEmpty, summaryData],
  )

  const displayOrders = useMemo(
    () => (isEmpty ? [defaultEmptyOrder] : orderBookOrders),
    [isEmpty, orderBookOrders],
  )

  // Benchmark ISIN is the same for all bids and asks for a given ISIN, if the ISIN has a benchmark
  const benchmarkIsin = useMemo(
    () =>
      asks[0]?.spread_benchmark_isin ?? bids[0]?.spread_benchmark_isin ?? null,
    [asks, bids],
  )

  return (
    <OrderBookContent
      benchmarkIsin={benchmarkIsin}
      hasBorder={hasBorder}
      collapsible={collapsible}
      rows={rows}
      minNumBonds={minNumBonds}
      maxNumBonds={maxNumBonds}
      onRowAction={onRowAction}
      className={className}
      baseClassName={baseClassName}
      isButtonDisabled={isButtonDisabled}
      showVenuesColumn={showVenuesColumn}
      alwaysShowVenuesColumnForTrader={alwaysShowVenuesColumnForTrader}
      showSource={showSource}
      initialOrders={displayOrders}
      initialSummaryData={displaySummaryData}
      initialIsEmpty={isEmpty}
      status={status}
      orderSide={orderSide}
      showActionButtonsOnAllRows={showActionButtonsOnAllRows}
      hideActionButtonsForNonApplicableTradeSide={
        hideActionButtonsForNonApplicableTradeSide
      }
      showPreAndPostFeeValues={showPreAndPostFeeValues}
      titleText={titleText}
      secondaryText={secondaryText}
      isQuoteSnapshot={isQuoteSnapshot}
      showControlPanel={showControlPanel}
      onCollapse={onCollapse}
    />
  )
}

// Separate the content component to use the Zustand store
function OrderBookContent({
  benchmarkIsin,
  hasBorder = false,
  collapsible = true,
  rows,
  minNumBonds,
  maxNumBonds,
  onRowAction,
  className,
  baseClassName,
  isButtonDisabled = false,
  showVenuesColumn = false,
  alwaysShowVenuesColumnForTrader = false,
  showSource = false,
  initialOrders,
  initialSummaryData,
  initialIsEmpty,
  status,
  orderSide,
  showActionButtonsOnAllRows = false,
  hideActionButtonsForNonApplicableTradeSide = false,
  showPreAndPostFeeValues = false,
  titleText,
  secondaryText,
  isQuoteSnapshot,
  showControlPanel = false,
  onCollapse,
}: {
  benchmarkIsin: string | null
  hasBorder?: boolean
  collapsible?: boolean
  rows?: number
  minNumBonds?: number
  maxNumBonds?: number
  onRowAction?: (
    orderData: OrderBookRowItem,
    side: QuoteSide,
    traderProfile: MesaTraderProfile,
  ) => Promise<void>
  className?: string
  baseClassName?: string
  isButtonDisabled?: boolean
  showVenuesColumn?: boolean
  alwaysShowVenuesColumnForTrader?: boolean
  showSource?: boolean
  initialOrders: OrderBookRowItem[]
  initialSummaryData: OrderSummaryData
  initialIsEmpty: boolean
  status?: TraderViewStatus | AdvisorViewStatus
  orderSide?: OrderSide.Buy | OrderSide.Sell
  showActionButtonsOnAllRows?: boolean
  hideActionButtonsForNonApplicableTradeSide?: boolean
  showPreAndPostFeeValues?: boolean
  titleText?: string
  secondaryText?: string
  isQuoteSnapshot?: boolean
  showControlPanel?: boolean
  onCollapse?: () => void
}) {
  const {
    isCollapsed,
    toggleCollapse,
    activePanel,
    selectSide,
    summaryData,
    orders,
  } = useOrderBookState(
    initialOrders,
    initialSummaryData,
    initialIsEmpty,
    status,
    orderSide,
  )
  // Show disabled quotes by default
  const DEFAULT_FILTER_DISABLED_QUOTES = false
  const controlPanelProps = useControlPanel(DEFAULT_FILTER_DISABLED_QUOTES)
  const { enrichedFilteredAsks, enrichedFilteredBids, canFilter } =
    useEnrichedFilteredBidsAsks({
      orders: orders,
      minNumBonds,
      maxNumBonds,
      shouldFilter: controlPanelProps.filterNonActionable,
    })

  const userArchetype = useUserArchetype()
  const isTrader = userArchetype === UserArchetype.Trader

  // Trader visibility for the venue column is gated on active panel by default
  // to protect screens with limited horizontal real-estate (trading flows).
  // Callers that have room for the extra column on the combined tab can opt in
  // via `alwaysShowVenuesColumnForTrader`.
  const finalShowVenuesColumn =
    showVenuesColumn ||
    (isTrader &&
      (alwaysShowVenuesColumnForTrader || activePanel !== ActivePanelType.Both))

  const showMbrAndIncrement = isTrader

  const handleToggleCollapse = () => {
    toggleCollapse()
    onCollapse?.()
  }

  return (
    <div className={baseClassName}>
      <OrderBookContainer hasBorder={hasBorder} className={className}>
        {titleText && (
          <OrderBookTitle titleText={titleText} secondaryText={secondaryText} />
        )}
        {collapsible && (
          <OrderBookHeader
            isCollapsed={isCollapsed}
            onToggleCollapse={handleToggleCollapse}
          />
        )}
        {/* hide controls when they don't do anything (no data to filter) */}
        {showControlPanel && canFilter && (
          <ControlPanel {...controlPanelProps} />
        )}
        <OrderBookSummaryBanner
          activePanel={activePanel}
          selectSide={selectSide}
          summaryData={summaryData}
        />
        <OrderBookTables
          activePanel={activePanel}
          bids={enrichedFilteredBids}
          asks={enrichedFilteredAsks}
          orders={orders}
          isCollapsed={isCollapsed}
          rows={rows}
          onRowAction={onRowAction}
          isButtonDisabled={isButtonDisabled}
          showVenuesColumn={finalShowVenuesColumn}
          showSource={showSource}
          showMbrAndIncrement={showMbrAndIncrement}
          benchmarkIsin={benchmarkIsin}
          orderSide={orderSide}
          showActionButtonsOnAllRows={showActionButtonsOnAllRows}
          hideActionButtonsForNonApplicableTradeSide={
            hideActionButtonsForNonApplicableTradeSide
          }
          showPreAndPostFeeValues={showPreAndPostFeeValues}
          isQuoteSnapshot={isQuoteSnapshot}
        />
      </OrderBookContainer>
    </div>
  )
}
